import { useState, useEffect } from "react";
import {
  getFilingDetails,
  updateFilingDetails,
  getLeadDetails,
  completeFilingDetailsConfirmation,
  type UpdateFilingDetailsPayload,
  type FilingDetailsResponse,
} from "../../api/discussion";
import { normalizeApiDateToIsoForForm } from "../../utils/filingApiDates";
import { toast } from "sonner";
import { getWorkflowStatus } from "api/applications";

const LABEL_STYLE =
  "text-xs font-normal leading-5 text-[#222222] flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BASE =
  "box-border flex flex-row items-center gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
const PLACEHOLDER_CLASS = "text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]";
const TEXT_CLASS = "text-[12px] md:text-xs font-normal leading-5 text-[#222222]";

const APPLICANT_TYPE_OPTIONS = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  { value: "LIMITED_LIABILITY_PARTNERSHIP", label: "Limited Liability Partnership" },
  { value: "OTHERS", label: "Others (manually enter)" },
];

function TextField({
  label,
  value,
  onChange,
  placeholder = "Text Here",
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label className={LABEL_STYLE} style={LABEL_FEATURES}>
        {label}
      </label>
      <div className={INPUT_BASE}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          className={`min-w-0 flex-1 bg-transparent focus:outline-none ${
            value ? TEXT_CLASS : PLACEHOLDER_CLASS
          } ${readOnly ? "cursor-default" : ""}`}
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
      </div>
    </div>
  );
}


type ConfirmationOfFilingDetailsViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
  /** Opens Filing Details to Client (step 3) so the user can edit data saved there. */
  onGoToEditFilingDetails?: () => void;
};

export default function ConfirmationOfFilingDetailsView({
  leadId,
  onSuccess,
  onSentStatusChange,
  onGoToEditFilingDetails,
}: ConfirmationOfFilingDetailsViewProps) {
  const [applicantName, setApplicantName] = useState("");
  const [applicantType, setApplicantType] = useState("");
  const [applicantTypeOther, setApplicantTypeOther] = useState("");
  const [address, setAddress] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [designation, setDesignation] = useState("");
  const [mark, setMark] = useState("");
  const [userDatesPerMark, setUserDatesPerMark] = useState<
    {
      classNumber: number;
      typeOfMark: string;
      userDateType?: string;
      date?: string;
      supportingDocuments?: { docNo: number; docName: string; docDetail: string; docDate: string }[];
    }[]
  >([]);

  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);

  const applyFilingResponse = (
    filingRes: FilingDetailsResponse,
    leadRes?: { lead?: { phone?: string } }
  ) => {
    const fd = filingRes.filingDetails;
    setApplicantName(fd.applicant ?? "");
    setApplicantType(fd.applicantType ?? "");
    setApplicantTypeOther(fd.applicantTypeOther ?? "");
    setAddress(fd.applicantAddressLine ?? "");
    setSignatoryName(fd.signatoryName ?? "");
    setDesignation(fd.signatoryDesignation ?? "");
    setMark(filingRes.fetched?.mark ?? "");
    const udpm = (fd.userDatesPerMark ?? []).map((item) => ({
      classNumber: item.classNumber,
      typeOfMark: item.typeOfMark,
      userDateType: item.userDateType,
      date: item.date
        ? normalizeApiDateToIsoForForm(item.date) ?? item.date
        : undefined,
      supportingDocuments: (item.supportingDocuments ?? []).map((doc) => ({
        ...doc,
        docDate: normalizeApiDateToIsoForForm(doc.docDate) ?? doc.docDate,
      })),
    }));
    setUserDatesPerMark(udpm);
  };

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([getFilingDetails(leadId), getLeadDetails(leadId)])
      .then(([filingRes, leadRes]) => {
        if (cancelled) return;
        applyFilingResponse(filingRes, leadRes);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load filing details");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      onSentStatusChange?.(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getWorkflowStatus(leadId);
        const step = status.steps.find(
          (s) => s.slug === "S2_04_FILING_DETAILS_CONFIRMATION"
        );
        if (!cancelled && step) {
          onSentStatusChange?.(step.isCompleted);
        }
      } catch {
        if (!cancelled) {
          onSentStatusChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, onSentStatusChange]);

  const buildConfirmationFilingDetailsPayload =
    (): UpdateFilingDetailsPayload => ({
    applicant: applicantName || undefined,
    applicantType: applicantType || undefined,
    applicantAddressLine: address || undefined,
    signatoryName: signatoryName || undefined,
    signatoryDesignation: designation || undefined,
    userDatesPerMark:
      userDatesPerMark.length > 0
        ? userDatesPerMark.map((item) => {
            const hasDate = !!item.date;
            const type =
              item.userDateType ??
              (hasDate ? "SPECIFIC_DATE" : "PROPOSED_TO_BE_USED");
            return {
              classNumber: item.classNumber,
              typeOfMark: item.typeOfMark,
              userDateType: type,
              date: item.date,
              supportingDocuments:
                item.supportingDocuments &&
                item.supportingDocuments.length > 0
                  ? item.supportingDocuments.map((doc) => ({
                      ...doc,
                      docDate:
                        normalizeApiDateToIsoForForm(doc.docDate) ??
                        doc.docDate,
                    }))
                  : undefined,
            };
          })
        : undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    setSaving(true);
    try {
      const payload = buildConfirmationFilingDetailsPayload();
      const updated = await updateFilingDetails(leadId, payload);
      applyFilingResponse(updated);
      await completeFilingDetailsConfirmation(leadId);
      toast.success("Filing details confirmation saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading filing details...</p>
      </div>
    );
  }

  const applicantTypeLabel =
    applicantType === "OTHERS" && applicantTypeOther.trim()
      ? applicantTypeOther.trim()
      : APPLICANT_TYPE_OPTIONS.find((o) => o.value === applicantType)?.label ??
        applicantType ??
        "—";

  return (
    <form
      id="confirmation-of-filing-details-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div
        className="flex flex-col gap-3 rounded-[10px] border border-[#E8E6F4] bg-[#F7F7FC] p-4 text-[12px] leading-5 text-[#222222]"
        style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
      >
        <p className="font-medium text-[#222222]">Confirmation of filing details (read-only)</p>
        <p className="text-[#444]">
          Data from <span className="font-medium">Filing Details to Client</span> (step 3)—not editable
          here. Change it in step 3 if needed, then return.
        </p>
        {onGoToEditFilingDetails && (
          <div>
            <button
              type="button"
              onClick={onGoToEditFilingDetails}
              className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#222222] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-gray-50"
            >
              Edit filing details in step 3
            </button>
          </div>
        )}
      </div>

      {/* Row 1: Applicant Name | Applicant Type (read-only — edit in step 3) */}
      <div className="flex flex-col md:flex-row md:items-center w-full gap-4 md:gap-5">
        <TextField
          label="Applicant Name"
          value={applicantName}
          onChange={() => {}}
          placeholder="Text Here"
          readOnly
        />
        <TextField
          label="Applicant Type"
          value={applicantTypeLabel}
          onChange={() => {}}
          readOnly
        />
      </div>

      {/* Row 2: Address (full width) */}
      <div className="flex flex-col w-full gap-1">
        <TextField
          label="Address"
          value={address}
          onChange={() => {}}
          placeholder="Text Here"
          readOnly
        />
      </div>

      {/* Row 3: Signatory Name | Designation */}
      <div className="flex flex-col md:flex-row md:items-center w-full gap-4 md:gap-5">
        <TextField
          label="Signatory Name"
          value={signatoryName}
          onChange={() => {}}
          readOnly
        />
        <TextField
          label="Designation"
          value={designation}
          onChange={() => {}}
          readOnly
        />
      </div>

      {/* Row 4: Mark (summary; per-class detail below) */}
      <div className="flex flex-col md:flex-row md:items-center w-full gap-4 md:gap-5">
        <TextField label="Mark" value={mark} onChange={() => {}} readOnly />
      </div>

      {/* User Date & Supporting Documents per Mark – read-only for confirmation */}
      {userDatesPerMark.length > 0 && (
        <div
          className="flex flex-col w-full"
          style={{ fontFamily: "Inter, sans-serif", fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        >
          <h3 className="text-sm font-medium text-[#222222]">
            User Date & Supporting Documents
          </h3>
          <div className="flex flex-col gap-4 mt-1">
            {/* Header - hidden on mobile */}
            <div className="hidden md:flex gap-5 items-center shrink-0 text-[#222] text-sm font-medium leading-5">
              <div className="w-[160px] shrink-0">Classes</div>
              <div className="flex-1 min-w-0">Type Mark</div>
              <div className="w-[182px] shrink-0">User Date</div>
              <div className="flex-1 min-w-0 shrink-0 whitespace-nowrap">
                List of Supporting Documents
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {userDatesPerMark.map((item, idx) => (
                <div
                  key={`${item.classNumber}-${item.typeOfMark}-${idx}`}
                  className="flex flex-col md:flex-row md:gap-5 md:items-center gap-3 w-full p-4 md:p-0 rounded-[6px] border border-[#EEE] md:border-0 bg-white md:bg-transparent"
                >
                  <div className="flex flex-col gap-1 md:contents">
                    <span className="md:hidden text-xs font-normal text-[#222222]">Classes</span>
                    <div className="flex shrink-0 w-full md:w-[160px] items-center min-h-[44px] md:min-h-0 bg-[#f7f7f7] border border-[#eee] rounded-[6px] p-2.5">
                      <span className="text-[12px] md:text-sm text-[#222] whitespace-nowrap">
                        Class {item.classNumber}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 md:contents flex-1 min-w-0">
                    <span className="md:hidden text-xs font-normal text-[#222222]">Type Mark</span>
                    <div className="flex flex-1 min-w-0 items-center min-h-[44px] md:min-h-0 h-9 px-2.5 py-2 border border-[#eee] rounded-[6px] bg-white text-[12px] md:text-xs text-[#222]">
                      {item.typeOfMark || "—"}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 md:contents shrink-0 w-full md:w-[182px]">
                    <span className="md:hidden text-xs font-normal text-[#222222]">User Date</span>
                    <div className="flex shrink-0 w-full md:w-[182px] items-center min-h-[44px] md:min-h-0 h-9 px-2.5 py-2 border border-[#eee] rounded-[6px] bg-white text-[12px] md:text-xs text-[#222]">
                      {item.date
                        ? new Date(item.date).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : item.userDateType === "PROPOSED_TO_BE_USED"
                        ? "Proposed to be used"
                        : "—"}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 flex-1 min-w-0 w-full">
                    <span className="md:hidden text-xs font-normal text-[#222222]">List of Supporting Documents</span>
                    <div className="flex-1 min-w-0 flex flex-col gap-1">
                    {item.supportingDocuments &&
                    item.supportingDocuments.length > 0 ? (
                      item.supportingDocuments.map((doc) => (
                        <div key={`${doc.docNo}-${doc.docName}`} className="text-[12px] md:text-xs break-words">
                          <span className="font-medium text-[#222]">{doc.docName}</span>
                          <span className="text-[#6D6D6D]">
                            {" "}
                            – {doc.docDetail}
                          </span>
                          <span className="text-[#6D6D6D]"> ({doc.docDate})</span>
                        </div>
                      ))
                    ) : (
                      <span className="text-[12px] md:text-xs text-[#6D6D6D]">—</span>
                    )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {saving && <p className="text-xs text-[#6D6D6D]">Saving...</p>}
    </form>
  );
}
